 
import { ProcedureInstance } from '@/backend/models/procedureInstances/procedureInstance';
import { PROCEDURETYPE, ProcedureInstanceBaseParams, ProcedureInstanceBase, ProcedureState } from '@/backend/models/procedureInstances/procedureInstanceBase';
import { ProcedureInstanceBuilder } from '@/backend/models/procedureInstances/procedureInstanceBuilder';
import { ProcedureTemplateInstance } from '@/backend/models/procedureInstances/procedureTemplateInstance';
import { create } from 'zustand';
import { ProcedureInstanceController } from '../controllers/procedureInstanceController';
import { ProcedureInstanceCreateFromTemplateReply, ProcedureInstanceCreateRequest, ProcedureInstanceResponse } from '@/backend/express/controllers/procedureInstanceController';
import { useGlobalStore } from './GlobalStore';
import { useRequirementsStore } from './RequirementsStore';
import { useProjectStore } from './ProjectStore';
import { I18n } from '@/i18n/I18n';
import { FusaToastType } from '../toasts/types';
import { ProcedureRun } from '@/backend/models/procedureInstances/procedureRun';
import { embedOnlyLink } from '@/frontend/embed/embedApp';

type ProcedureListStore = {
  procedures: ProcedureInstance[],
  templates: ProcedureTemplateInstance[],
  runs: ProcedureRun[],
  fetchProcedureItems: (procedureType: PROCEDURETYPE) => Promise<void>
  reset: () => void
  deleteProcedureItem: (procedure: ProcedureInstanceBase) => Promise<boolean>
  duplicateProcedureItem: (procedureId: string) => Promise<void>
  createProcedureItem: (request: ProcedureInstanceCreateRequest) => Promise<void>
  createProcedureFromTemplate: (procedure: ProcedureInstance, functionalInterfaceImplementationAssignments: { [key: string]: string }) => Promise<ProcedureInstanceCreateFromTemplateReply>
  initializeProcedureFromTemplate: (templateId: string, projectId?: string) => Promise<ProcedureInstanceResponse>
  setProceduresByType: (procedure: ProcedureInstanceBaseParams) => void
  setTemplates: (templates: ProcedureInstanceBaseParams[]) => void
  setProcedures: (procedures: ProcedureInstanceBaseParams[]) => void
  setRuns: (runs: ProcedureInstanceBaseParams[]) => void
  handleTemplateResponse: (templates: ProcedureInstanceBaseParams[]) => void
  filterAndSortProcedures: (procedures: ProcedureInstanceBaseParams[]) => ProcedureInstanceBase[]
  readPublishedTemplatesForEnterprise: () => Promise<void>;
  readAllGeneratedProceduresForProject: (projectId: string) => Promise<void>;
  readAllProcedureInstancesAssignedToProject: (projectId: string) => Promise<void>;
};

const initialState = {
  procedures: [],
  templates: [],
  runs: [],
};

export const defaultSort = (a: ProcedureInstanceBase, b: ProcedureInstanceBase) => {
  if (a.viewedAt && b.viewedAt) {
    return a.viewedAt > b.viewedAt ? -1 : 1;
  }

  if (a.viewedAt && !b.viewedAt) {
    return -1;
  }

  if (!a.viewedAt && b.viewedAt) {
    return 1;
  }

  return a?.updatedAt && b?.updatedAt ? a.updatedAt > b.updatedAt ? -1 : 1 : 0;
};

export const useProcedureListStore = create<ProcedureListStore>((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  fetchProcedureItems: async (procedureType: PROCEDURETYPE) => {
    const response = await new ProcedureInstanceController().readAllByType(procedureType);
    if (response.errors) return;
    if (procedureType === PROCEDURETYPE.TEMPLATE) {
      get().setTemplates(response.procedureInstances);
      // useTemplateProcedureListStore.setState({ allTemplateProcedureVersions: response.versions.map((version) => new TemplateProcedureVersion(version)) });
    } else if (procedureType === PROCEDURETYPE.RUN) {
      get().setRuns(response.procedureInstances);
    } else {
      get().setProcedures(response.procedureInstances);
    }
  },
  setProceduresByType: (procedure: ProcedureInstanceBaseParams) => {
    if (procedure.procedureType === PROCEDURETYPE.TEMPLATE) {
      get().setTemplates([procedure, ...get().templates]);
    } else if (procedure.procedureType === PROCEDURETYPE.RUN) {
      get().setRuns([procedure, ...get().runs]);
    } else {
      get().setProcedures([procedure, ...get().procedures]);
    }
  },
  setTemplates: (templates: ProcedureInstanceBaseParams[]) => {
    set({ templates: get().filterAndSortProcedures(templates) as ProcedureTemplateInstance[] });
  },
  setProcedures: (procedures: ProcedureInstanceBaseParams[]) => {
    set({ procedures: get().filterAndSortProcedures(procedures) });
  },
  setRuns: (runs: ProcedureInstanceBaseParams[]) => {
    set({ runs: get().filterAndSortProcedures(runs) as ProcedureRun[] });
  },
  handleTemplateResponse: (templates: ProcedureInstanceBaseParams[]) => {
    const existingIds = new Set(get().templates.map(t => t.id));
    const newTemplates = templates.filter(p => !existingIds.has(p.id));
    if (newTemplates.length > 0) {
      get().setTemplates([...get().templates, ...newTemplates]);
    }
  },
  filterAndSortProcedures: (procedures: ProcedureInstanceBaseParams[]) => {
    return procedures.map((procedure) => new ProcedureInstanceBuilder().build(procedure))
    .filter((procedure) => !!procedure)
    .filter((procedure) => procedure.state !== ProcedureState.INITIALIZED)
    .sort(defaultSort);
  },
  deleteProcedureItem: async (procedure) => {
    const response = await  new ProcedureInstanceController().delete(procedure.id);

    // A 404 means the procedure is already gone from the server, which is the
    // state the delete was trying to reach, so prune it locally instead of
    // leaving it on screen. Without this a delete that errors once and then
    // reports the procedure as missing on retry keeps it in the list forever.
    // Every other failure (in a report, assigned to a project, forbidden)
    // leaves the procedure in place and surfaces its own error toast.
    const alreadyDeleted = response.status === 404;
    if (response.errors && !alreadyDeleted) return false;

    if (procedure.procedureType === PROCEDURETYPE.PROCEDURE) {
      const procedures = get().procedures.filter((proc) => proc.id !== procedure.id);
      set({ procedures });
    }
    if (procedure.procedureType === PROCEDURETYPE.TEMPLATE) {
      const templates = get().templates.filter((template) => template.id !== procedure.id);
      set({ templates });
    }
    if (procedure.procedureType === PROCEDURETYPE.RUN) {
      const runs = get().runs.filter((run) => run.id !== procedure.id);
      set({ runs });
    }

    // The procedure row is hard deleted, and the database cascades its
    // requirement and project assignments with it. Those assignments are
    // cached in sibling stores that have no other invalidation signal, so
    // prune them here or the assigned procedures views keep showing the
    // deleted procedure until the page is reloaded.
    useRequirementsStore.getState().removeAssignmentsForProcedure(procedure.id);
    useProjectStore.getState().removeAssignmentsForProcedure(procedure.id);

    return true;
  },
  duplicateProcedureItem: async (procedureId) => {
    const response = await new ProcedureInstanceController().duplicate(procedureId);
    if (response.errors) return;
    get().setProceduresByType(response.procedureInstance);
    // useLabelStore.getState().fetchLabelAssignments();
  },
  createProcedureItem: async (request) => {
    const response = await new ProcedureInstanceController().create(request);
    if (!response.errors && response.status === 200) {
      get().setProceduresByType(response.procedureInstance);
    }
  },
  createProcedureFromTemplate: async (procedure, functionalInterfaceImplementationAssignments?: { [key: string]: string }) => {
    return new ProcedureInstanceController().createProcedureFromTemplate(procedure.id, functionalInterfaceImplementationAssignments).then((response) => {
      if (!response.errors && response.procedureInstance && response.status === 200) {
        get().setProceduresByType(response.procedureInstance);
      }
      if (response.templateError) {
        useGlobalStore.getState().addToast({
          message: response.templateError.message,
          link: embedOnlyLink(`/procedures/scaffold/${procedure.procedureTemplateId}#${response.templateError.itemId}`),
          linkText: response.templateError.additionalDetail,    
          header: I18n.get('error'),
          type: FusaToastType.ERROR,
        });
      }
      return response;
    });
  },
  initializeProcedureFromTemplate: async (templateId: string, projectId?: string) => {
    const response = await new ProcedureInstanceController().initializeFromTemplate(templateId, projectId);
    if (!response.errors) {
      get().setProceduresByType(response.procedureInstance);
    }
    return response;
  },
  readPublishedTemplatesForEnterprise: async () => {
    const response = await new ProcedureInstanceController().readPublishedTemplatesForEnterprise();
    if (response.errors) return;
    get().handleTemplateResponse(response.procedureInstances);
  },
  readAllGeneratedProceduresForProject: async (projectId) => {
    const response = await new ProcedureInstanceController().readAllGeneratedProceduresForProject(projectId);
    if (response.errors) return;
    get().setProcedures(response.procedureInstances);
  },
  readAllProcedureInstancesAssignedToProject: async (projectId) => {
    const response = await new ProcedureInstanceController().readAllAssignedToProject(projectId);
    if (response.errors) return;
    get().handleTemplateResponse(response.procedureInstances);
  },
}));

